/**
 * Tag-provider folder-level browse extractors and mapper.
 *
 *  - `extractSubfolderEntries(value)` shared across the provider
 *    and UDT folder mappers.
 *  - `extractDirectUdtInstanceEntries(value)` projects the
 *    `directUdtInstances` CALL output into typed
 *    {@link PlantProviderUdtInstanceEntry}s.
 *  - `mapPlantProviderFolderNodeResponse(raw)` projects the single
 *    row returned by `PLANT_PROVIDER_FOLDER_NODE_CYPHER` into the
 *    full {@link PlantProviderFolderNodeResponse} bundle.
 *
 * Pure functions, no RTK / no Neo4j driver — trivially unit-testable.
 */

import { extractNodeList, extractRawNode, leafTagSegment, toIntCount } from '../graph-row'
import { coalesceTagFolder } from './provider-tags'
import type {
  CypherReadResult,
  PlantFolderSubfolderEntry,
  PlantProviderFolderNodeResponse,
  PlantProviderTag,
  PlantProviderUdtInstanceEntry
} from '../types'

/**
 * Cypher `collect({name, tagCount})` from `PLANT_PROVIDER_FOLDER_NODE_CYPHER`
 * (and the `subfolders` CALL in `PLANT_UDT_FOLDER_NODE_CYPHER`).
 * Filters sentinel rows and coerces `tagCount` into a non-negative int.
 */
export function extractSubfolderEntries(value: unknown): PlantFolderSubfolderEntry[] {
  if (!Array.isArray(value)) return []
  const out: PlantFolderSubfolderEntry[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const name = typeof obj['name'] === 'string' ? obj['name'].trim() : ''
    if (!name) continue
    out.push({ name, tagCount: toIntCount(obj['tagCount']) })
  }
  return out
}

/**
 * Project the `{instance, definition, memberCount}` shape returned by
 * `PLANT_PROVIDER_FOLDER_NODE_CYPHER`'s `directUdtInstances` CALL into
 * `PlantProviderUdtInstanceEntry[]`. Each row describes one
 * `:UDTInstance` anchored at exactly the current tag-folder path,
 * carrying enough info for the explorer to drill into the instance's
 * member tree without an extra round-trip (`definitionId` is the node
 * id of the `:UDTDefinition` reachable via `INSTANCE_OF`).
 *
 * Defensively skips rows missing an instance id or a sentinel-null
 * bundle. `definitionId` / `definitionName` are tolerated as null
 * because a UDT instance can outlive its definition (e.g. mid-ingest),
 * in which case the explorer renders the folder as empty rather than
 * crashing.
 */
export function extractDirectUdtInstanceEntries(value: unknown): PlantProviderUdtInstanceEntry[] {
  if (!Array.isArray(value)) return []
  const out: PlantProviderUdtInstanceEntry[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const instanceRaw = extractRawNode(obj['instance'])
    if (!instanceRaw) continue
    const id =
      typeof instanceRaw['id'] === 'string'
        ? (instanceRaw['id'] as string)
        : typeof instanceRaw['_id'] === 'string'
          ? (instanceRaw['_id'] as string)
          : null
    if (!id) continue
    if (seen.has(id)) continue
    seen.add(id)
    const instanceName =
      typeof instanceRaw['instance_name'] === 'string' && instanceRaw['instance_name']
        ? (instanceRaw['instance_name'] as string)
        : typeof instanceRaw['name'] === 'string'
          ? (leafTagSegment(instanceRaw['name'] as string) ?? (instanceRaw['name'] as string))
          : id
    const defRaw = extractRawNode(obj['definition'])
    const definitionId =
      defRaw && typeof defRaw['id'] === 'string'
        ? (defRaw['id'] as string)
        : defRaw && typeof defRaw['_id'] === 'string'
          ? (defRaw['_id'] as string)
          : null
    const definitionName =
      defRaw && typeof defRaw['name'] === 'string' ? (defRaw['name'] as string) : null
    out.push({
      id,
      instanceName,
      definitionId,
      definitionName,
      memberCount: toIntCount(obj['memberCount'])
    })
  }
  return out
}

/**
 * Project the single row returned by `PLANT_PROVIDER_FOLDER_NODE_CYPHER`
 * into a typed {@link PlantProviderFolderNodeResponse}: the direct
 * tags at this folder, the direct `:UDTInstance` children, and the
 * one-level-deep subfolders with recursive leaf counts.
 */
export function mapPlantProviderFolderNodeResponse(
  raw: CypherReadResult
): PlantProviderFolderNodeResponse {
  const row = raw.results[0] ?? {}
  const directTags = extractNodeList<PlantProviderTag>(row['directTags']).map(coalesceTagFolder)
  const subfolders = extractSubfolderEntries(row['subfolders'])
  const directUdtInstances = extractDirectUdtInstanceEntries(row['directUdtInstances'])
  return { directTags, subfolders, directUdtInstances }
}
